import { Scene, SceneData } from './Scene';

const MAX_HISTORY = 50;

/**
 * Snapshot-based undo/redo history.
 * Stores serialized scene states on a stack.
 */
export class History {
  private undoStack: SceneData[] = [];
  private redoStack: SceneData[] = [];

  /** Take a snapshot of the current scene (call before a mutation) */
  push(scene: Scene): void {
    this.undoStack.push(scene.toJSON());
    if (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift();
    }
    // Any new action clears the redo stack
    this.redoStack.length = 0;
  }

  /** Undo: restore the previous state, save current to redo stack.
   *  Returns the restored scene, or null if nothing to undo. */
  undo(currentScene: Scene): Scene | null {
    const prev = this.undoStack.pop();
    if (!prev) return null;
    this.redoStack.push(currentScene.toJSON());
    return Scene.fromJSON(prev);
  }

  /** Redo: restore the next state, save current to undo stack.
   *  Returns the restored scene, or null if nothing to redo. */
  redo(currentScene: Scene): Scene | null {
    const next = this.redoStack.pop();
    if (!next) return null;
    this.undoStack.push(currentScene.toJSON());
    return Scene.fromJSON(next);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }
}
